import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { GetBaseURL } from "../components/utils/urlUtils";

// ─── Public API (no auth) ────────────────────────────────────────────────────

const publicApi = axios.create({ baseURL: GetBaseURL() || "" });

// ─── Types ───────────────────────────────────────────────────────────────────

interface BookingQuestion {
  label: string;
  type: "text" | "textarea" | "select";
  required: boolean;
  options?: string[];
}

interface BookingTypeData {
  _id: string;
  name: string;
  slug: string;
  duration: number;
  location: { type: "video" | "phone" | "in_person" | "custom"; details?: string | null };
  description?: string;
  color?: string;
  questions: BookingQuestion[];
  collectPhone?: boolean;
  collectMessage?: boolean;
  confirmationMessage?: string;
}

interface TeamMemberData {
  name: string;
  email?: string;
}

interface FirmBrandingData {
  name: string;
  slug?: string;
  branding?: any;
}

interface BookingData {
  firm: FirmBrandingData;
  bookingType: BookingTypeData;
  teamMember: TeamMemberData;
}

interface SlotDay {
  date: string;
  slots: string[];
}

interface BookingResult {
  booking: {
    _id: string;
    status: string;
    startAt: string;
    endAt: string;
    manageToken: string;
  };
  meetingUrl?: string | null;
  manageUrl: string;
  confirmationMessage?: string;
}

type Phase = "loading" | "date" | "time" | "form" | "confirmation" | "error";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatSlotTime(isoOrTime: string): string {
  const d = new Date(isoOrTime);
  if (!isNaN(d.getTime())) {
    return d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }
  const [hourStr, minuteStr] = isoOrTime.split(":");
  let hour = parseInt(hourStr, 10);
  const minute = minuteStr;
  const ampm = hour >= 12 ? "PM" : "AM";
  if (hour === 0) hour = 12;
  else if (hour > 12) hour -= 12;
  return `${hour}:${minute} ${ampm}`;
}

function formatDateReadable(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function getTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "America/Toronto";
  }
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function isBeforeToday(dateStr: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + "T00:00:00");
  return d < today;
}

function getTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getSlotPeriod(isoOrTime: string): "morning" | "afternoon" | "evening" {
  const d = new Date(isoOrTime);
  if (!isNaN(d.getTime())) {
    const h = d.getHours();
    if (h < 12) return "morning";
    if (h < 17) return "afternoon";
    return "evening";
  }
  const hour = parseInt(isoOrTime.split(":")[0], 10);
  if (hour < 12) return "morning";
  if (hour < 17) return "afternoon";
  return "evening";
}

function getLocationLabel(locationType: string | undefined, detail?: string): string {
  switch (locationType) {
    case "video":
      return detail || "Video Call";
    case "phone":
      return detail || "Phone Call";
    case "in_person":
      return detail || "In Person";
    default:
      return detail || "Meeting";
  }
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
    : null;
}

function colorWithAlpha(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

// ─── SVG Icons (inline, no external deps) ────────────────────────────────────

const ChevronLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const CheckCircleIcon = ({ color }: { color: string }) => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const LoaderIcon = ({ color }: { color: string }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "embed-spin 1s linear infinite" }}>
    <line x1="12" y1="2" x2="12" y2="6" />
    <line x1="12" y1="18" x2="12" y2="22" />
    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
    <line x1="2" y1="12" x2="6" y2="12" />
    <line x1="18" y1="12" x2="22" y2="12" />
    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
  </svg>
);

const AlertIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const EmbedBookingPage: React.FC = () => {
  const { firmSlug, bookingSlug } = useParams<{
    firmSlug: string;
    bookingSlug: string;
  }>();
  const [searchParams] = useSearchParams();
  const primaryColor = searchParams.get("color")
    ? `#${searchParams.get("color")!.replace(/^#/, "")}`
    : "#0F5132";
  const frameId = searchParams.get("frameId") || "";

  const containerRef = useRef<HTMLDivElement>(null);

  // ─── State ────────────────────────────────────────────────────────────────

  const [phase, setPhase] = useState<Phase>("loading");
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [slots, setSlots] = useState<SlotDay[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );

  // Form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState<BookingResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [slotError, setSlotError] = useState<string | null>(null);

  const timezone = useMemo(() => getTimezone(), []);

  // ─── postMessage resize ────────────────────────────────────────────────────

  const sendResize = useCallback(() => {
    try {
      window.parent.postMessage(
        {
          type: "kavoa-resize",
          height: document.body.scrollHeight,
          id: frameId,
        },
        "*"
      );
    } catch {
      // silently fail if not in iframe
    }
  }, [frameId]);

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      sendResize();
    });
    observer.observe(document.body);
    sendResize();
    return () => observer.disconnect();
  }, [sendResize]);

  // Re-send on phase change
  useEffect(() => {
    const timer = setTimeout(sendResize, 100);
    return () => clearTimeout(timer);
  }, [phase, selectedDate, selectedTime, sendResize]);

  // ─── Available dates set ──────────────────────────────────────────────────

  const availableDates = useMemo(() => {
    const set = new Set<string>();
    slots.forEach((s) => {
      if (s.slots.length > 0) set.add(s.date);
    });
    return set;
  }, [slots]);

  // ─── Selected date's slots grouped by period ─────────────────────────────

  const selectedDateSlots = useMemo(() => {
    if (!selectedDate) return [];
    const day = slots.find((s) => s.date === selectedDate);
    return day ? day.slots : [];
  }, [slots, selectedDate]);

  const groupedSlots = useMemo(() => {
    const groups: { morning: string[]; afternoon: string[]; evening: string[] } = {
      morning: [],
      afternoon: [],
      evening: [],
    };
    selectedDateSlots.forEach((slot) => {
      groups[getSlotPeriod(slot)].push(slot);
    });
    return groups;
  }, [selectedDateSlots]);

  // ─── Fetch booking type on mount ──────────────────────────────────────────

  const fetchSlots = useCallback(
    async (monthDate: Date) => {
      if (!firmSlug || !bookingSlug) return;
      const startDate = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, "0")}-01`;
      const end = new Date(monthDate.getFullYear(), monthDate.getMonth() + 2, 0);
      const endDate = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`;

      try {
        const res = await publicApi.get(`/book/${firmSlug}/${bookingSlug}/slots`, {
          params: { startDate, endDate },
        });
        setSlots(res.data.data || res.data.slots || []);
      } catch {
        setSlots([]);
      }
    },
    [firmSlug, bookingSlug]
  );

  useEffect(() => {
    const init = async () => {
      if (!firmSlug || !bookingSlug) {
        setPhase("error");
        return;
      }

      try {
        const res = await publicApi.get(`/book/${firmSlug}/${bookingSlug}`);
        setBookingData(res.data);

        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        setCurrentMonth(monthStart);
        await fetchSlots(monthStart);
        setPhase("date");
      } catch (err: any) {
        if (err.response?.status === 404) {
          setError("This booking page doesn't exist or is no longer available.");
        } else {
          setError("Failed to load booking page. Please try again later.");
        }
        setPhase("error");
      }
    };

    init();
  }, [firmSlug, bookingSlug, fetchSlots]);

  // ─── Month change ────────────────────────────────────────────────────────

  const handleMonthChange = useCallback(
    (newMonth: Date) => {
      setCurrentMonth(newMonth);
      fetchSlots(newMonth);
    },
    [fetchSlots]
  );

  // ─── Phase transitions ───────────────────────────────────────────────────

  const handleSelectDate = (date: string) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setPhase("time");
  };

  const handleSelectTime = (time: string) => {
    setSelectedTime(time);
    setPhase("form");
  };

  const handleBackToDate = () => {
    setSelectedTime(null);
    setSlotError(null);
    setPhase("date");
  };

  const handleBackToTime = () => {
    setSlotError(null);
    setPhase("time");
  };

  // ─── Form submission ─────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firmSlug || !bookingSlug || !selectedDate || !selectedTime) return;

    setSubmitting(true);
    setSlotError(null);

    try {
      const formattedAnswers: { questionLabel: string; answer: string }[] = [];

      if (bookingData?.bookingType.collectMessage !== false && message.trim()) {
        formattedAnswers.push({ questionLabel: "Message", answer: message.trim() });
      }

      bookingData?.bookingType.questions.forEach((q) => {
        const val = answers[q.label] || "";
        if (val) {
          formattedAnswers.push({ questionLabel: q.label, answer: val });
        }
      });

      const res = await publicApi.post(`/book/${firmSlug}/${bookingSlug}`, {
        bookedByName: name.trim(),
        bookedByEmail: email.trim(),
        bookedByPhone: phone.trim() || undefined,
        startAt: selectedTime,
        timezone,
        answers: formattedAnswers,
      });

      setBookingResult(res.data);
      setPhase("confirmation");
    } catch (err: any) {
      if (err.response?.status === 409) {
        setSlotError("This time slot is no longer available. Please select another time.");
        setPhase("time");
        fetchSlots(currentMonth);
      } else {
        setSlotError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "Something went wrong. Please try again."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Styles ───────────────────────────────────────────────────────────────

  const styles = {
    container: {
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      background: "#ffffff",
      minHeight: "100px",
      color: "#1e293b",
    } as React.CSSProperties,
    card: {
      background: "#ffffff",
      borderRadius: "16px",
      border: "1px solid #e2e8f0",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)",
    } as React.CSSProperties,
    heading: {
      fontSize: "15px",
      fontWeight: 600,
      color: "#1e293b",
      margin: "0 0 16px 0",
    } as React.CSSProperties,
    subheading: {
      fontSize: "13px",
      color: "#94a3b8",
      margin: "0 0 16px 0",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    } as React.CSSProperties,
    backButton: {
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      fontSize: "13px",
      color: "#64748b",
      background: "none",
      border: "none",
      cursor: "pointer",
      padding: "0",
      marginBottom: "16px",
      transition: "color 0.15s",
    } as React.CSSProperties,
    input: {
      width: "100%",
      borderRadius: "10px",
      border: "1px solid #e2e8f0",
      padding: "10px 14px",
      fontSize: "14px",
      color: "#1e293b",
      outline: "none",
      transition: "border-color 0.15s, box-shadow 0.15s",
      boxSizing: "border-box" as const,
    } as React.CSSProperties,
    label: {
      display: "block",
      fontSize: "13px",
      fontWeight: 500,
      color: "#475569",
      marginBottom: "6px",
    } as React.CSSProperties,
    required: {
      color: "#ef4444",
      marginLeft: "2px",
    } as React.CSSProperties,
    errorBanner: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      background: "#fef2f2",
      border: "1px solid #fecaca",
      borderRadius: "10px",
      padding: "10px 14px",
      fontSize: "13px",
      color: "#b91c1c",
      marginBottom: "16px",
    } as React.CSSProperties,
    periodLabel: {
      fontSize: "11px",
      fontWeight: 600,
      color: "#94a3b8",
      textTransform: "uppercase" as const,
      letterSpacing: "0.05em",
      marginBottom: "8px",
      marginTop: "4px",
    } as React.CSSProperties,
  };

  // ─── Render: Loading ──────────────────────────────────────────────────────

  if (phase === "loading") {
    return (
      <div ref={containerRef} style={{ ...styles.container, display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 20px" }}>
        <style>{`@keyframes embed-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        <div style={{ textAlign: "center" }}>
          <LoaderIcon color={primaryColor} />
          <p style={{ fontSize: "13px", color: "#94a3b8", marginTop: "12px" }}>Loading available times...</p>
        </div>
      </div>
    );
  }

  // ─── Render: Error ────────────────────────────────────────────────────────

  if (phase === "error") {
    return (
      <div ref={containerRef} style={{ ...styles.container, display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 20px" }}>
        <div style={{ textAlign: "center", maxWidth: "360px" }}>
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              background: "#fef2f2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#1e293b", margin: "0 0 8px" }}>Booking Unavailable</h2>
          <p style={{ fontSize: "14px", color: "#64748b", margin: 0, lineHeight: 1.5 }}>
            {error || "This booking page doesn't exist or is no longer available."}
          </p>
        </div>
      </div>
    );
  }

  if (!bookingData) return null;

  const bt = bookingData.bookingType;

  // ─── Render: Info header (compact) ────────────────────────────────────────

  const InfoHeader = () => (
    <div style={{ padding: "20px 24px", borderBottom: "1px solid #f1f5f9" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <div
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "10px",
            background: primaryColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <span style={{ color: "#fff", fontWeight: 700, fontSize: "14px" }}>
            {bookingData.firm.name.charAt(0).toUpperCase()}
          </span>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: "15px", fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {bt.name}
          </div>
          <div style={{ fontSize: "13px", color: "#64748b", display: "flex", alignItems: "center", gap: "12px", marginTop: "2px" }}>
            <span>{bookingData.teamMember.name}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
              <ClockIcon /> {bt.duration} min
            </span>
            <span style={{ color: "#94a3b8" }}>
              {getLocationLabel(bt.location?.type, bt.location?.details || undefined)}
            </span>
          </div>
        </div>
      </div>
      {bt.description && (
        <p style={{ fontSize: "13px", color: "#64748b", margin: "10px 0 0", lineHeight: 1.5 }}>
          {bt.description}
        </p>
      )}
    </div>
  );

  // ─── Render: Calendar ─────────────────────────────────────────────────────

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const todayISO = getTodayISO();

    const monthLabel = currentMonth.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });

    const now = new Date();
    const canGoPrev = !(
      new Date(year, month - 1, 1).getFullYear() < now.getFullYear() ||
      (new Date(year, month - 1, 1).getFullYear() === now.getFullYear() &&
        new Date(year, month - 1, 1).getMonth() < now.getMonth())
    );

    const days: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);

    const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    return (
      <div style={{ padding: "20px 24px" }}>
        <h3 style={styles.heading}>Select a Date</h3>

        {/* Month navigation */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
          <button
            onClick={() => canGoPrev && handleMonthChange(new Date(year, month - 1, 1))}
            disabled={!canGoPrev}
            style={{
              background: "none",
              border: "none",
              cursor: canGoPrev ? "pointer" : "not-allowed",
              opacity: canGoPrev ? 1 : 0.3,
              padding: "6px",
              borderRadius: "8px",
              display: "flex",
              color: "#475569",
            }}
          >
            <ChevronLeftIcon />
          </button>
          <span style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>{monthLabel}</span>
          <button
            onClick={() => handleMonthChange(new Date(year, month + 1, 1))}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "6px",
              borderRadius: "8px",
              display: "flex",
              color: "#475569",
            }}
          >
            <ChevronRightIcon />
          </button>
        </div>

        {/* Weekday headers */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: "4px" }}>
          {weekdays.map((wd) => (
            <div
              key={wd}
              style={{
                textAlign: "center",
                fontSize: "11px",
                fontWeight: 600,
                color: "#94a3b8",
                padding: "4px 0",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {wd}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px" }}>
          {days.map((day, idx) => {
            if (day === null) return <div key={`empty-${idx}`} />;

            const dateISO = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            const isPast = isBeforeToday(dateISO);
            const hasSlots = availableDates.has(dateISO);
            const isSelected = selectedDate === dateISO;
            const isToday = dateISO === todayISO;
            const isClickable = !isPast && hasSlots;

            return (
              <button
                key={dateISO}
                onClick={() => isClickable && handleSelectDate(dateISO)}
                disabled={!isClickable}
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "40px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: isSelected ? 700 : isClickable ? 500 : 400,
                  color: isSelected ? "#fff" : isClickable ? "#1e293b" : "#cbd5e1",
                  background: isSelected ? primaryColor : "transparent",
                  border: isToday && !isSelected ? "1px solid #cbd5e1" : "1px solid transparent",
                  cursor: isClickable ? "pointer" : "default",
                  transition: "all 0.15s",
                  outline: "none",
                }}
                onMouseEnter={(e) => {
                  if (isClickable && !isSelected) {
                    (e.currentTarget as HTMLButtonElement).style.background = colorWithAlpha(primaryColor, 0.08);
                  }
                }}
                onMouseLeave={(e) => {
                  if (isClickable && !isSelected) {
                    (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                  }
                }}
              >
                <span>{day}</span>
                {hasSlots && !isSelected && (
                  <span
                    style={{
                      position: "absolute",
                      bottom: "4px",
                      width: "4px",
                      height: "4px",
                      borderRadius: "50%",
                      background: primaryColor,
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── Render: Time Slots ───────────────────────────────────────────────────

  const renderTimeSlots = () => {
    const periodLabels = { morning: "Morning", afternoon: "Afternoon", evening: "Evening" };
    const periods = (["morning", "afternoon", "evening"] as const).filter(
      (p) => groupedSlots[p].length > 0
    );

    return (
      <div style={{ padding: "20px 24px" }}>
        <button
          onClick={handleBackToDate}
          style={styles.backButton}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#1e293b"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#64748b"; }}
        >
          <ArrowLeftIcon /> Back
        </button>

        <h3 style={{ ...styles.heading, marginBottom: "4px" }}>
          {selectedDate && formatDateReadable(selectedDate)}
        </h3>
        <p style={styles.subheading}>
          <GlobeIcon /> {timezone.replace(/_/g, " ")}
        </p>

        {slotError && (
          <div style={styles.errorBanner}>
            <AlertIcon />
            {slotError}
          </div>
        )}

        {selectedDateSlots.length === 0 ? (
          <p style={{ fontSize: "13px", color: "#64748b" }}>
            No times available for this date. Please select another date.
          </p>
        ) : (
          <div style={{ maxHeight: "340px", overflowY: "auto" }}>
            {periods.map((period) => (
              <div key={period} style={{ marginBottom: "16px" }}>
                <div style={styles.periodLabel}>{periodLabels[period]}</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                  {groupedSlots[period].map((time) => {
                    const isActive = selectedTime === time;
                    return (
                      <button
                        key={time}
                        onClick={() => handleSelectTime(time)}
                        style={{
                          padding: "10px 8px",
                          borderRadius: "10px",
                          border: `1.5px solid ${isActive ? primaryColor : "#e2e8f0"}`,
                          background: isActive ? colorWithAlpha(primaryColor, 0.06) : "#fff",
                          color: isActive ? primaryColor : "#475569",
                          fontSize: "13px",
                          fontWeight: 600,
                          cursor: "pointer",
                          transition: "all 0.15s",
                          outline: "none",
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            (e.currentTarget as HTMLButtonElement).style.borderColor = primaryColor;
                            (e.currentTarget as HTMLButtonElement).style.color = primaryColor;
                            (e.currentTarget as HTMLButtonElement).style.background = colorWithAlpha(primaryColor, 0.04);
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            (e.currentTarget as HTMLButtonElement).style.borderColor = "#e2e8f0";
                            (e.currentTarget as HTMLButtonElement).style.color = "#475569";
                            (e.currentTarget as HTMLButtonElement).style.background = "#fff";
                          }
                        }}
                      >
                        {formatSlotTime(time)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ─── Render: Form ─────────────────────────────────────────────────────────

  const renderForm = () => {
    const inputFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      e.currentTarget.style.borderColor = primaryColor;
      e.currentTarget.style.boxShadow = `0 0 0 3px ${colorWithAlpha(primaryColor, 0.1)}`;
    };
    const inputBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      e.currentTarget.style.borderColor = "#e2e8f0";
      e.currentTarget.style.boxShadow = "none";
    };

    return (
      <div style={{ padding: "20px 24px" }}>
        <button
          onClick={handleBackToTime}
          style={styles.backButton}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#1e293b"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#64748b"; }}
        >
          <ArrowLeftIcon /> Back
        </button>

        {/* Selected date/time summary */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            padding: "12px 16px",
            background: colorWithAlpha(primaryColor, 0.04),
            borderRadius: "10px",
            marginBottom: "20px",
            border: `1px solid ${colorWithAlpha(primaryColor, 0.1)}`,
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>
            <CalendarIcon />
            {selectedDate && formatDateReadable(selectedDate)}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>
            <ClockIcon />
            {selectedTime && formatSlotTime(selectedTime)}
          </span>
        </div>

        <h3 style={styles.heading}>Your Details</h3>

        {slotError && (
          <div style={styles.errorBanner}>
            <AlertIcon />
            {slotError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {/* Name */}
            <div>
              <label style={styles.label}>
                Name<span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                style={styles.input}
                onFocus={inputFocus}
                onBlur={inputBlur}
              />
            </div>

            {/* Email */}
            <div>
              <label style={styles.label}>
                Email<span style={styles.required}>*</span>
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={styles.input}
                onFocus={inputFocus}
                onBlur={inputBlur}
              />
            </div>

            {/* Phone */}
            {bt.collectPhone !== false && (
              <div>
                <label style={styles.label}>Phone</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(optional)"
                  style={styles.input}
                  onFocus={inputFocus}
                  onBlur={inputBlur}
                />
              </div>
            )}

            {/* Message */}
            {bt.collectMessage !== false && (
              <div>
                <label style={styles.label}>Message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  placeholder="Anything you'd like us to know before the meeting (optional)"
                  style={{
                    ...styles.input,
                    resize: "none" as const,
                    lineHeight: 1.5,
                  }}
                  onFocus={inputFocus as any}
                  onBlur={inputBlur as any}
                />
              </div>
            )}

            {/* Custom questions */}
            {bt.questions.map((q) => (
              <div key={q.label}>
                <label style={styles.label}>
                  {q.label}
                  {q.required && <span style={styles.required}>*</span>}
                </label>

                {q.type === "text" && (
                  <input
                    type="text"
                    required={q.required}
                    value={answers[q.label] || ""}
                    onChange={(e) => setAnswers((prev) => ({ ...prev, [q.label]: e.target.value }))}
                    style={styles.input}
                    onFocus={inputFocus}
                    onBlur={inputBlur}
                  />
                )}

                {q.type === "textarea" && (
                  <textarea
                    required={q.required}
                    value={answers[q.label] || ""}
                    onChange={(e) => setAnswers((prev) => ({ ...prev, [q.label]: e.target.value }))}
                    rows={3}
                    style={{ ...styles.input, resize: "none" as const, lineHeight: 1.5 }}
                    onFocus={inputFocus as any}
                    onBlur={inputBlur as any}
                  />
                )}

                {q.type === "select" && q.options && (
                  <select
                    required={q.required}
                    value={answers[q.label] || ""}
                    onChange={(e) => setAnswers((prev) => ({ ...prev, [q.label]: e.target.value }))}
                    style={{ ...styles.input, appearance: "none" as const, backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%2364748b'%3e%3cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z' clip-rule='evenodd'/%3e%3c/svg%3e\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", backgroundSize: "16px", paddingRight: "36px" }}
                    onFocus={inputFocus as any}
                    onBlur={inputBlur as any}
                  >
                    <option value="">Select an option</option>
                    {q.options.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ))}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "10px",
                border: "none",
                background: primaryColor,
                color: "#fff",
                fontSize: "14px",
                fontWeight: 600,
                cursor: submitting ? "not-allowed" : "pointer",
                opacity: submitting ? 0.7 : 1,
                transition: "opacity 0.15s, transform 0.1s",
                marginTop: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
              onMouseEnter={(e) => {
                if (!submitting) (e.currentTarget as HTMLButtonElement).style.opacity = "0.9";
              }}
              onMouseLeave={(e) => {
                if (!submitting) (e.currentTarget as HTMLButtonElement).style.opacity = "1";
              }}
            >
              {submitting && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "embed-spin 1s linear infinite" }}>
                  <line x1="12" y1="2" x2="12" y2="6" />
                  <line x1="12" y1="18" x2="12" y2="22" />
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                  <line x1="2" y1="12" x2="6" y2="12" />
                  <line x1="18" y1="12" x2="22" y2="12" />
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
                  <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                </svg>
              )}
              {submitting ? "Scheduling..." : "Schedule Booking"}
            </button>
          </div>
        </form>
      </div>
    );
  };

  // ─── Render: Confirmation ─────────────────────────────────────────────────

  const renderConfirmation = () => {
    const startDate = bookingResult ? new Date(bookingResult.booking.startAt) : null;
    const confirmMsg =
      bookingResult?.confirmationMessage ||
      bookingData.bookingType.confirmationMessage ||
      "Your booking has been confirmed. You will receive a confirmation email shortly.";

    return (
      <div style={{ padding: "40px 24px", textAlign: "center" }}>
        <div
          style={{
            width: "72px",
            height: "72px",
            borderRadius: "50%",
            background: colorWithAlpha(primaryColor, 0.08),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
          }}
        >
          <CheckCircleIcon color={primaryColor} />
        </div>

        <h2 style={{ fontSize: "20px", fontWeight: 700, color: "#1e293b", margin: "0 0 8px" }}>
          You're Booked!
        </h2>

        <p style={{ fontSize: "14px", color: "#64748b", margin: "0 0 24px", lineHeight: 1.6, maxWidth: "380px", marginLeft: "auto", marginRight: "auto" }}>
          {confirmMsg}
        </p>

        {/* Booking details card */}
        <div
          style={{
            background: "#f8fafc",
            borderRadius: "12px",
            padding: "20px",
            maxWidth: "360px",
            margin: "0 auto",
            textAlign: "left",
          }}
        >
          <div style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b", marginBottom: "12px" }}>
            {bt.name}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#475569" }}>
              <CalendarIcon />
              {startDate
                ? startDate.toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })
                : ""}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#475569" }}>
              <ClockIcon />
              {startDate
                ? startDate.toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })
                : ""}{" "}
              ({bt.duration} min)
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#475569" }}>
              <GlobeIcon />
              {timezone.replace(/_/g, " ")}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─── Main render ──────────────────────────────────────────────────────────

  return (
    <div ref={containerRef} style={styles.container}>
      <style>{`
        @keyframes embed-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #ffffff; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      <div style={{ padding: "16px", maxWidth: "480px", margin: "0 auto" }}>
        <div style={styles.card}>
          <InfoHeader />

          {phase === "date" && renderCalendar()}
          {phase === "time" && renderTimeSlots()}
          {phase === "form" && renderForm()}
          {phase === "confirmation" && renderConfirmation()}
        </div>
      </div>
    </div>
  );
};

export default EmbedBookingPage;
